import { useQuery, useMutation } from 'react-query';
import {
  addMinistryFocusLogic,
  getCustomerPermissions,
  getCustomerProfileData,
  getProfileData,
} from '../api/userApi';
import { useGetLinkedOrgs } from './organizationHooks';
import { PERMISSIONS } from '../../constants';

const userQueries = {
  base: () => ({
    queryKey: ['user'],
  }),
  permissions: (customerId, linkedOrgs) => ({
    queryKey: [...userQueries.base().queryKey, 'permissions'],
    queryFn: () => getCustomerPermissions(customerId, linkedOrgs),
  }),
};

/* User Profile */
export function useGetUserProfile() {
  const {
    data: profileResponse,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => getProfileData(),
    staleTime: Infinity,
    retry: true,
  });

  return {
    data: profileResponse,
    isLoading: isProfileLoading,
    isError: isProfileError,
  };
}

/* [NEW] User Profile */
export function useGetIndividualProfile() {
  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useQuery({
    queryKey: ['customer', 'user', 'profile'],
    queryFn: () => getCustomerProfileData(),
    staleTime: Infinity,
    retry: false,
  });

  return {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  };
}

/* Add Ministry Focus */
export function useAddMinistryFocus() {
  return useMutation({
    mutationFn: addMinistryFocusLogic,
    onSuccess: async (data) => {
      return { data };
    },
    onError: (error) => {
      return error;
    },
  });
}

/* Get Customer Permissions */
/*
  Params:
    customerId - required
    permission - optional (string) permission displayId to filter on
    organizationId - optional (string) organizationId to filter on
    accountNumber - optional (string or array of strings) accountNumber(s) to filter on
*/
export function useGetCustomerPermissions(
  customerId,
  { permission, organizationId, accountNumber } = {}
) {
  const {
    data: linkedOrgs,
    isError: isLinkedOrgError,
    isLoading: isLinkedOrgsLoading,
  } = useGetLinkedOrgs();

  const { data: permissions, isLoading, isError } = useQuery({
    ...userQueries.permissions(customerId, linkedOrgs),
    enabled: !!customerId && !!linkedOrgs?.length,
    staleTime: Infinity,
    select: (data) => {
      const mapped = data.map((payloadPermission) => {
        const match = PERMISSIONS.find(
          (p) => p.permissionId === payloadPermission.permissionId
        );
        return match
          ? { ...payloadPermission, displayId: match.displayId }
          : payloadPermission;
      });

      if (!permission && !organizationId && !accountNumber) return mapped;

      const accountNumbers = Array.isArray(accountNumber)
        ? accountNumber
        : accountNumber
        ? [accountNumber]
        : [];

      const matchedPermissions = mapped.filter((perm) => {
        const matchesDisplay = permission
          ? perm.displayId === permission
          : true;
        const matchesOrg = organizationId
          ? perm?.permissionScope?.organization === organizationId ||
            perm?.permissionScope?.organization === '*'
          : true;
        const matchesAccount =
          accountNumbers.length > 0
            ? accountNumbers.includes(perm?.permissionScope?.account)
            : true;

        return matchesDisplay && matchesOrg && matchesAccount;
      });

      return matchedPermissions.length === 1
        ? matchedPermissions[0]?.hasAccess
        : matchedPermissions;
    },
  });

  return {
    data: permissions,
    isLoading: isLoading || isLinkedOrgsLoading,
    isError: isError || isLinkedOrgError,
  };
}
